import { notification } from 'antd';

import { AES, enc, mode, SHA256 } from 'crypto-js';
import {
  APP_ENV,
  DEFAULT_COMPANY_ID,
  DEFAULT_ENVIRONMENT,
  SECRET_KEY,
  TOKEN_KEY,
} from './constant';
import { translate } from './i18n/i18n';

const t = translate(['common']);

export const getRandomValues = (length) => {
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const encryptPassword = (password) => {
  const salt = getRandomValues(16);
  const key = enc.Utf8.parse(SECRET_KEY);
  return (
    salt +
    AES.encrypt(password, key, {
      mode: mode.CBC,
      iv: enc.Utf8.parse(salt),
    }).toString()
  );
};

export const randomString = () => {
  return Math.random().toString(26).substr(3, 8);
};

export const getShortName = (name) => {
  if (!name) return '';
  const [fName, lName] = name.split(' ');
  return `${fName[0]}${lName?.length > 0 ? lName[0] : ''}`;
};

/** Function to generate unique color from a given string */

const hRange = [0, 360];
const sRange = [50, 75];
const lRange = [25, 60];

const getHashOfString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(hash);
  return hash;
};

const normalizeHash = (hash, min, max) => {
  return Math.floor((hash % (max - min)) + min);
};

const generateHSL = (name) => {
  const hash = getHashOfString(name);
  const h = normalizeHash(hash, hRange[0], hRange[1]);
  const s = normalizeHash(hash, sRange[0], sRange[1]);
  const l = normalizeHash(hash, lRange[0], lRange[1]);
  return [h, s, l];
};

const HSLtoString = (hsl) => {
  return `hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)`;
};

export const stringToColor = (string) => {
  return HSLtoString(generateHSL(string));
};

export const getTagColorStyles = (str) => {
  if (!str) {
    return {};
  }

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = Math.abs(hash) % 360;

  return {
    backgroundColor: `hsl(${hue}, 70%, 92%)`, // light background
    color: `hsl(${hue}, 40%, 30%)`, // dark text
    border: 'none',
  };
};

/** End of Function to generate unique color from a given string */

export const makePct = (pct) => {
  return (pct * 100).toFixed(1);
};

export const getPct = (a, b = 1) => {
  try {
    return a / b;
  } catch {
    return 0;
  }
};

export const removeAuthToken = () => {
  const key = `${getCompanySlugFromURL(window.location)}${TOKEN_KEY}`;
  localStorage.removeItem(key);
};

export const setAuthToken = (value) => {
  const key = `${getCompanySlugFromURL(window.location)}${TOKEN_KEY}`;
  return localStorage.setItem(key, value);
};

export const getAuthToken = () => {
  const key = `${getCompanySlugFromURL(window.location)}${TOKEN_KEY}`;
  return localStorage.getItem(key) || undefined;
};

export const getRandomNumber = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const formatMessageList = (xs, key) => {
  return xs.reduce((rv, x) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
};

export const range = (size, startAt = 0) => {
  return [...Array(size).keys()].map((i) => i + startAt);
};

export const getChoiceType = (choice) => {
  const choices = {
    0: t(`questionChoice.0`),
    1: t(`questionChoice.1`),
    2: t('questionChoice.2'),
    3: t('questionChoice.3'),
    4: t('questionChoice.4'),
    5: t('questionChoice.5'),
    6: t('questionChoice.6'),
    7: t('questionChoice.7'),
    9: t('questionChoice.9'),
  };
  return choices[choice];
};

export const getAnswerType = (choice) => {
  const choices = {
    POS: t(`positive`),
    NEG: t('negative'),
    NEU: t('neutral'),
  };
  return choices[choice];
};

export const TagsTypeName = {
  T: t('tagType.team'),
  D: t('tagType.department'),
  G: t('tagType.location'),
};

const getLabelByDurationType = () => {
  const durationDict = {
    1: '7 days',
    2: '30 days',
    3: '180 days',
    4: '365 days',
    5: '365 days',
  };
  return durationDict[durationType];
};

export const dynamicMultipleSort = (fields) => (a, b) =>
  fields
    .map((o) => {
      let dir = 1;
      if (o[0] === '-') {
        dir = -1;
        o = o.substring(1);
      }
      return a[o] > b[o] ? dir : a[o] < b[o] ? -dir : 0;
    })
    .reduce((p, n) => (p ? p : n), 0);

export const generateQueryParams = (params) => {
  const newParams = {};
  for (const key in params) {
    newParams[key] = params[key];
    if (Array.isArray(params[key]) && params[key].length > 0) {
      newParams[key] = params[key].toString();
    }
  }
  return newParams;
};

export const openNotification = (
  message,
  description = null,
  type = 'success',
  placement = 'topRight',
) => {
  notification.destroy();
  notification[type]({
    message,
    description,
    placement,
  });
};

export const getOpportuneCompanyNameFromURL = (url) => {
  // Create a URL object
  const parsedUrl = new URL(url);

  // Get the pathname (e.g., '/opportune/login/asdasd')
  const pathname = parsedUrl.pathname;

  // Split the pathname by '/' and return the second segment
  const segments = pathname.split('/');

  // Check if the second segment exists (index 1 because index 0 is empty from split)
  if (segments.length > 1) {
    return segments[1]; // This will be the dynamic part ('opportune' in this case)
  }
  return null; // Return null if no segment is found
};

export const generateLinkText = (value) => {
  const slug = getOpportuneCompanyNameFromURL(window.location);
  if ([DEFAULT_ENVIRONMENT.OPPORTUNE].includes(APP_ENV)) {
    return `/${slug}${value}`;
  }
  return value;
};

export const getCompanySlugFromURL = () => {
  if (DEFAULT_COMPANY_ID) return DEFAULT_COMPANY_ID;
  if ([DEFAULT_ENVIRONMENT.OPPORTUNE].includes(APP_ENV)) {
    const slug = getOpportuneCompanyNameFromURL(window.location);
    return slug;
  }
  return APP_ENV === DEFAULT_ENVIRONMENT.DEV
    ? 'localhost'
    : window.location.hostname.split('-dashboard.elrin.ai')[0];
};

export const getElrinText = () => {
  if ([DEFAULT_ENVIRONMENT.OPPORTUNE].includes(APP_ENV)) {
    return t('epm');
  }
  return t('elrin');
};

export const sanitizeValues = (values) => {
  if (typeof values === 'string') {
    // Remove HTML tags from strings
    return values.replace(/<[^>]*>/g, '');
  } else if (Array.isArray(values)) {
    // Recursively sanitize array elements
    return values.map((item) => sanitizeValues(item));
  } else if (typeof values === 'object' && values !== null) {
    // Recursively sanitize object properties
    return Object.fromEntries(
      Object.entries(values).map(([key, value]) => [
        key,
        sanitizeValues(value),
      ]),
    );
  }
  // Return other types (numbers, booleans, etc.) as-is
  return values;
};

export const formatTime = (timeInSeconds = 0) => {
  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor((timeInSeconds % 3600) / 60);
  const seconds = Math.floor(timeInSeconds % 60);

  // Append leading zeros to minutes and seconds
  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  const formattedSeconds = seconds.toString().padStart(2, '0');

  // Return HH:MM:SS if hours are present, otherwise MM:SS
  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const formatQuestionText = (str, company_name, first_name) => {
  if (!str) return '';

  const replacements = {
    company__name: company_name,
    company__names: company_name,
    first_name: first_name,
    first_names: first_name,
  };

  return str.replace(/{{\s*(\w+)\s*}}/g, (match, key) => {
    const value = replacements[key];
    return value ? `<b>${value}</b>` : match;
  });
};
